import { useState, useEffect } from 'react';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'About', id: 'about-section' },
    { label: 'Services', id: 'services-section' },
    { label: 'Why ZJF IT Services', id: 'why-choose-section' },
    { label: 'Process', id: 'process-section' },
    { label: 'Our Work', id: 'portfolio-section' },
    { label: 'Industries', id: 'industries-section' },
    { label: 'Partnership', id: 'partnership-section' },
    { label: 'Contact', id: 'contact-section' },
  ];

  const scrollToSection = (id: string) => {
    const section = document.getElementById(id);
    section?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const scrollToContact = () => scrollToSection('contact-section');
  const scrollToPartnership = () => scrollToSection('partnership-section');

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-zinc-950">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/3f8fcc818a3c9af4f72c4f44c412b834.jpg"
          alt="IT Infrastructure Background"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/60 via-zinc-950/70 to-zinc-950/90"></div>
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-zinc-950 shadow-lg shadow-black/20' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/51b92d7fefcf4905c242441f3c7dddfe/fca0ce1bda8298b6a686d97531ce5f08.png" 
                alt="ZJF IT Services Logo" 
                className="h-11 w-11 object-contain"
              />
              <h1 className="text-lg font-semibold text-white tracking-tight">
                ZJF IT Services LLC
              </h1>
            </div>
            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-7">
              {navLinks.map((link) => (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  onClick={(e) => { e.preventDefault(); scrollToSection(link.id); }}
                  className="text-sm font-medium text-gray-300 hover:text-orange-500 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="tel:973-750-4968"
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-md text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-base"></i>
                973-750-4968
              </a>
            </div>
            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden flex items-center justify-center w-10 h-10 text-white cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-zinc-950/95 backdrop-blur-sm border-t border-zinc-800">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  onClick={(e) => { e.preventDefault(); scrollToSection(link.id); }}
                  className="text-sm font-medium text-gray-300 hover:text-orange-500 transition-colors py-3 px-3 rounded-md hover:bg-zinc-800/50 cursor-pointer"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="tel:973-750-4968"
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-5 py-3 rounded-md text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer mt-2"
              >
                <i className="ri-phone-line text-base"></i>
                973-750-4968
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-32 text-center">
        <div className="max-w-5xl mx-auto">
          {/* Dispatch Guarantee Badge */}
          <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-5 py-2.5 mb-8">
            <i className="ri-time-line text-orange-500 text-lg"></i>
            <span className="text-sm font-semibold text-orange-500">24-48 Hour Dispatch Guarantee</span>
          </div>
          
          <h2 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight tracking-tight">
            Field Execution Partner for <span className="text-orange-500">IT Deployments</span>
          </h2>
          <p className="text-xl md:text-2xl text-orange-400 mb-4 font-semibold">
            White-Label IT Field Services for MSPs
          </p>
          <p className="text-lg md:text-xl text-gray-300 mb-12 leading-relaxed max-w-3xl mx-auto font-light">
            Reliable workstation rollouts, structured cabling, and network installations - serving NYC & Nationwide.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
            <button 
              onClick={scrollToContact}
              className="bg-orange-500 hover:bg-orange-600 text-white px-10 py-4 rounded-md text-base font-semibold transition-all duration-300 shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40 whitespace-nowrap cursor-pointer"
            >
              Request a Consultation
            </button>
            <button 
              onClick={scrollToPartnership}
              className="bg-transparent border-2 border-orange-500 hover:bg-orange-500 text-orange-500 hover:text-white px-10 py-4 rounded-md text-base font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Partner With Us
            </button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="animate-bounce">
          <i className="ri-arrow-down-line text-3xl text-orange-500"></i>
        </div>
      </div>
    </section>
  );
}
